import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Callout } from "@bednbody/ui/components/callout";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { MapPinIcon } from "@phosphor-icons/react";
import { Badge, type BadgeVariant } from "@bednbody/ui/components/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@bednbody/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Marker, ServiceMap } from "@bednbody/ui/service-map";
import { useAuth } from "@/lib/auth";
import { bookingStatusLabel } from "@/lib/bookingStatusLabels";
import {
  canReadBookingEvents,
  canSeeClientDetails,
  useBookingDetail,
  useBookingEvents,
  type BookingDetailData,
} from "@/features/bookings/booking-detail.hooks";
import { InServiceChangesCard } from "@/features/bookings/InServiceChangesCard";
import { TherapistEntriesCard } from "@/features/bookings/TherapistEntriesCard";
import { BookingDetailActions } from "./BookingDetailActions";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  pending: "warning",
  awaiting_therapist: "warning",
  awaiting_rider: "warning",
  dispatched: "info",
  en_route_to_client: "info",
  arrived: "info",
  in_session: "primary",
  completed: "success",
  cancelled: "destructive",
};

/** What each `booking_events` action is called on the timeline. */
const EVENT_ACTION_LABEL: Record<string, string> = {
  confirm_pick_up: "Therapist confirmed pick-up",
  confirm_drop_off: "Therapist confirmed drop-off",
  confirm_cash_received: "Therapist confirmed cash received",
  therapist_pickup_confirmed: "Therapist picked up after the session",
  therapist_return_completed: "Therapist returned",
};

function humanize(value: string): string {
  return value.replace(/_/g, " ");
}

function when(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" })
    : "—";
}

function peso(value: number): string {
  return `₱${value.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface BookingDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The popup reads the booking itself — see `useBookingDetail`. */
  bookingId: string | null;
}

/**
 * The one place a booking is acted on. The Bookings table used to carry an
 * Actions column (Start session, Fix time, Mark completed) beside this popup's
 * own overrides; every action now lives here, under the data it acts on.
 */
export function BookingDetailModal({ isOpen, onClose, bookingId }: BookingDetailModalProps) {
  const { claims } = useAuth();
  const role = claims?.role ?? null;

  // The last booking shown, so the panel does not empty while it fades out.
  const lastId = useRef<string | null>(bookingId);
  useEffect(() => {
    if (bookingId) lastId.current = bookingId;
  }, [bookingId]);
  const id = bookingId ?? lastId.current;

  const detail = useBookingDetail(isOpen ? id : null, role);
  const events = useBookingEvents(isOpen ? id : null, role !== null && canReadBookingEvents(role));
  const row = detail.data ?? null;

  return (
    <Dialog open={isOpen && bookingId !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
        <DialogHeader className="border-b border-border px-6 py-4">
          <div className="flex flex-wrap items-center gap-2 pr-8">
            <DialogTitle>{row ? row.ref : "Booking"}</DialogTitle>
            {row ? (
              <Badge variant={STATUS_VARIANT[row.status] ?? "muted"}>
                {bookingStatusLabel(row.status)}
              </Badge>
            ) : null}
            {row?.bookingGroupId ? <Badge variant="outline">Group booking</Badge> : null}
          </div>
          <DialogDescription>
            {row
              ? `${row.serviceName ?? "Service"} · ${when(row.scheduledAt)} · ${row.hubName ?? "Hub"}`
              : "Loading booking…"}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {detail.isError ? (
            <Callout tone="danger" role="alert">
              {(detail.error as Error).message}
            </Callout>
          ) : !row ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32 motion-reduce:animate-none" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="space-y-4">
                <DetailCard title="Booking">
                  <Field label="Service">
                    {row.serviceName ?? "—"}
                    {row.durationMinutes ? ` · ${row.durationMinutes} min` : ""}
                  </Field>
                  <Field label="Scheduled">{when(row.scheduledAt)}</Field>
                  <Field label="Hub">
                    {row.hubName ?? "—"}
                    {row.hubRegion ? ` · ${row.hubRegion}` : ""}
                    {row.hubIsPilot ? " · Pilot" : ""}
                  </Field>
                  <Field label="Created">{when(row.createdAt)}</Field>
                  {row.completedAt ? (
                    <Field label="Completed">{when(row.completedAt)}</Field>
                  ) : null}
                  {row.cancelledAt ? (
                    <Field label="Cancelled">
                      {when(row.cancelledAt)}
                      {row.cancellationStage ? ` · ${humanize(row.cancellationStage)}` : ""}
                    </Field>
                  ) : null}
                </DetailCard>

                <DetailCard title="People">
                  {role !== null && canSeeClientDetails(role) ? (
                    <Field label="Client">{row.clientName ?? "—"}</Field>
                  ) : null}
                  <Field label="Therapist">{row.therapistName ?? "Not assigned"}</Field>
                  <Field label="Rider">{row.riderName ?? "Not assigned"}</Field>
                </DetailCard>

                <DetailCard title="Payment">
                  <Field label="Method">
                    <span className="capitalize">{humanize(row.paymentMethod)}</span>
                  </Field>
                  {/* Figures only for the roles 0285 grants them to. A hub
                      manager still sees HOW a booking pays, which is what cash
                      work needs, and never what it is worth. */}
                  {row.grossAmount !== null ? (
                    <Field label="Gross">{peso(row.grossAmount)}</Field>
                  ) : null}
                  {row.giftpassAmount !== null && row.giftpassAmount > 0 ? (
                    <Field label="GiftPass">−{peso(row.giftpassAmount)}</Field>
                  ) : null}
                  {row.convenienceFee !== null && row.convenienceFee > 0 ? (
                    <Field label="Convenience fee">{peso(row.convenienceFee)}</Field>
                  ) : null}
                  {row.amountDue !== null ? (
                    <Field label="Amount due">
                      <span className="font-semibold">{peso(row.amountDue)}</span>
                    </Field>
                  ) : null}
                  {row.refundStatus && row.refundStatus !== "none" ? (
                    <Field label="Refund">
                      <span className="capitalize">{humanize(row.refundStatus)}</span>
                    </Field>
                  ) : null}
                </DetailCard>
              </div>

              <div className="space-y-4">
                {/* S6 — each Therapist's state (0227) and the Superadmin-only
                    Replace (0230). */}
                <TherapistEntriesCard
                  bookingGroupId={row.bookingGroupId}
                  isSuperadmin={role === "superadmin"}
                />

                {/* 0282 — the Changes audit family: Extend, Upgrade, Add Service. */}
                <InServiceChangesCard
                  bookingId={row.id}
                  bookingGroupId={row.bookingGroupId}
                  canCancel={role === "admin" || role === "superadmin"}
                />

                <BookingDetailActions
                  booking={{
                    id: row.id,
                    ref: row.ref,
                    status: row.status,
                    hub_id: row.hubId,
                    rider_id: row.riderId,
                    gross_amount: row.grossAmount ?? undefined,
                    amount_due: row.amountDue,
                    payment_method: row.paymentMethod,
                  }}
                  onDone={onClose}
                />

                {role !== null && canSeeClientDetails(role) ? <LocationCard row={row} /> : null}

                {role !== null && canReadBookingEvents(role) ? (
                  <DetailCard title="Timeline">
                    {events.isPending ? (
                      <p className="text-sm text-muted-foreground">Loading…</p>
                    ) : (events.data ?? []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No events recorded.</p>
                    ) : (
                      <ol className="space-y-3">
                        {(events.data ?? []).map((e) => (
                          <li key={e.id} className="flex items-start gap-3">
                            <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium capitalize">
                                {e.action
                                  ? (EVENT_ACTION_LABEL[e.action] ?? humanize(e.action))
                                  : humanize(e.toStatus)}
                              </p>
                              <p className="text-xs text-muted-foreground">{when(e.createdAt)}</p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    )}
                  </DetailCard>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {row ? (
          <div className="border-t border-border px-6 py-3 text-xs text-muted-foreground">
            Booking ID <span className="font-mono">{row.id}</span>
            {row.clientRequestId ? (
              <>
                {" "}
                · Request ID <span className="font-mono">{row.clientRequestId}</span>
              </>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DetailCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-2">{children}</dl>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words">{children}</dd>
    </div>
  );
}

/** Address, rider notes and the pin — cross-hub roles only (data wall). */
function LocationCard({ row }: { row: BookingDetailData }) {
  return (
    <DetailCard title="Location">
      <Field label="Address">{row.addressText ?? "—"}</Field>
      {row.notesToRider ? <Field label="Notes to rider">{row.notesToRider}</Field> : null}
      {row.location ? (
        <div className="pt-2">
          <ServiceMap
            // Remount per booking: `initialViewState` is read once.
            key={row.id}
            className="h-64 w-full overflow-hidden rounded-xl border border-border"
            constrainToArea={false}
            initialViewState={{ latitude: row.location.lat, longitude: row.location.lng, zoom: 15 }}
            token={MAPBOX_TOKEN}
          >
            <Marker anchor="bottom" latitude={row.location.lat} longitude={row.location.lng}>
              <span className="flex size-9 items-center justify-center rounded-full border-2 border-white bg-primary text-primary-foreground shadow-md">
                <MapPinIcon className="size-5" weight="fill" />
              </span>
            </Marker>
          </ServiceMap>
          <p className="mt-1.5 font-mono text-xs text-muted-foreground">
            {row.location.lat.toFixed(6)}, {row.location.lng.toFixed(6)}
          </p>
        </div>
      ) : (
        <p className="pt-1 text-sm text-muted-foreground">No map pin recorded for this booking.</p>
      )}
    </DetailCard>
  );
}
